import { cloneDeep, isEmpty, mapValues } from 'lodash';
import { processContentState } from './processContentState.js';
import { v4 } from 'uuid';
import { WRAP, LINK_TYPE, IMAGE_TYPE, GALLERY_TYPE, VIDEO_TYPE, VERTICAL_EMBED_TYPE, POLL_TYPE, IMAGE_TYPE_LEGACY, VIDEO_TYPE_LEGACY, SOUND_CLOUD_TYPE } from '../consts.js';
import linkDataNormalizer from './dataNormalizers/linkDataNormalizer.js';
import imageDataNormalizer from './dataNormalizers/imageDataNormalizer.js';
import galleryDataNormalizer from './dataNormalizers/galleryDataNormalizer.js';
import videoDataNormalizer from './dataNormalizers/videoDataNormalizer.js';
import verticalEmbedDataNormalizer from './dataNormalizers/verticalEmbedDataNormalizer.js';
import pollsDataNormalizer from './dataNormalizers/pollsDataNormalizer.js';
import './removeInlineHeaderRanges.js';
import './block-processors.js';
import './linkify.js';
import '../urlValidators/urlValidators.js';
import './inlinePluginsRemover.js';
import './removeInvalidInlineStyles.js';
import '../version/versioningUtils.js';
import '../package-58f3e07f.js';
import 'ricos-schema';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const dataNormalizers = {
  [LINK_TYPE]: linkDataNormalizer,
  [IMAGE_TYPE]: imageDataNormalizer,
  [GALLERY_TYPE]: galleryDataNormalizer,
  [VIDEO_TYPE]: videoDataNormalizer,
  [VERTICAL_EMBED_TYPE]: verticalEmbedDataNormalizer,
  [POLL_TYPE]: pollsDataNormalizer
};
const normalizeComponentData = (type, componentData, config, version) => dataNormalizers[type](componentData, config, version);
const normalizeComponentConfig = (componentData) => {
  if (componentData.config) {
    return componentData;
  }
  const config = {};
  const { alignment, size, src, oembed } = componentData;
  if (alignment) {
    delete componentData.alignment;
    config.alignment = alignment;
    config.size = "small";
  } else {
    if (size) {
      delete componentData.size;
      if (size === "smallCenter") {
        config.size = "small";
        config.alignment = "center";
      } else if (size === "fullWidth") {
        config.size = "fullWidth";
        config.alignment = "center";
      }
    } else {
      config.size = src && src.width && src.width <= 740 ? "original" : "content";
      config.alignment = "center";
    }
  }
  const patch = { config };
  if (oembed) {
    delete componentData.url;
    delete componentData.oembed;
    patch.src = oembed.video_url;
    patch.metadata = { oembed };
  }
  return __spreadValues(__spreadValues({}, componentData), patch);
};
const entityTypeMap = {
  configNormalization: {
    [IMAGE_TYPE_LEGACY]: IMAGE_TYPE,
    [VIDEO_TYPE_LEGACY]: VIDEO_TYPE,
    [SOUND_CLOUD_TYPE]: VIDEO_TYPE
  },
  dataNormalization: {
    [LINK_TYPE]: LINK_TYPE,
    [IMAGE_TYPE]: IMAGE_TYPE,
    [GALLERY_TYPE]: GALLERY_TYPE,
    [VIDEO_TYPE]: VIDEO_TYPE,
    [VERTICAL_EMBED_TYPE]: VERTICAL_EMBED_TYPE,
    [POLL_TYPE]: POLL_TYPE
  }
};
const shouldNormalizeEntity = (entity, normalizationMap) => Object.keys(normalizationMap).includes(entity.type) && entity.data;
const shouldNormalizeEntityConfig = (entity) => shouldNormalizeEntity(entity, entityTypeMap.configNormalization);
const shouldNormalizeEntityData = (entity) => shouldNormalizeEntity(entity, entityTypeMap.dataNormalization);
const normalizeEntityMap = (entityMap, config, stateVersion) => {
  const normalizeType = (key, obj) => obj[key] || key;
  return mapValues(entityMap, (entity) => {
    var _a, _b, _c;
    let newEntity = entity;
    if (shouldNormalizeEntityConfig(entity)) {
      newEntity = __spreadProps(__spreadValues({}, entity), {
        type: normalizeType(entity.type, entityTypeMap.configNormalization),
        data: normalizeComponentConfig(entity.data)
      });
    } else if (shouldNormalizeEntityData(entity)) {
      newEntity = __spreadProps(__spreadValues({}, entity), {
        type: normalizeType(entity.type, entityTypeMap.dataNormalization),
        data: normalizeComponentData(entity.type, entity.data, config, stateVersion)
      });
    }
    if (((_a = newEntity == null ? void 0 : newEntity.data) == null ? void 0 : _a.config) && !((_c = (_b = newEntity == null ? void 0 : newEntity.data) == null ? void 0 : _b.config) == null ? void 0 : _c.textWrap)) {
      newEntity.data.config.textWrap = WRAP;
    }
    convertImageAnchorToLink(newEntity);
    return newEntity;
  });
};
const isImageAnchor = (entity) => {
  var _a, _b, _c, _d;
  return entity.type === "wix-draft-plugin-image" && !!((_b = (_a = entity.data) == null ? void 0 : _a.config) == null ? void 0 : _b.anchor) && !((_d = (_c = entity.data) == null ? void 0 : _c.config) == null ? void 0 : _d.link);
};
const convertImageAnchorToLink = (newEntity) => {
  if (isImageAnchor(newEntity)) {
    const _a = newEntity.data.config, { anchor } = _a, rest = __objRest(_a, ["anchor"]);
    newEntity.data = __spreadProps(__spreadValues({}, newEntity.data), {
      config: __spreadProps(__spreadValues({}, rest), {
        link: { anchor }
      })
    });
  }
};
var normalizeInitialState = (content, config = {}) => {
  const { blocks, entityMap, documentStyle, VERSION } = processContentState(
    cloneDeep(content),
    config
  );
  const contentState = {
    blocks,
    entityMap: normalizeEntityMap(entityMap, config, content.VERSION || "0.0.0"),
    VERSION,
    ID: content.ID || v4()
  };
  if (!isEmpty(documentStyle)) {
    contentState.documentStyle = documentStyle;
  }
  return contentState;
};

export { normalizeInitialState as default };
//# sourceMappingURL=normalizeInitialState.js.map
